import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAddress } from '@/contexts/AddressContext';
import { LoadingScreen } from './LoadingScreen';
import { Routes } from '@/routes/routeNames';

/**
 * AddressGate — wrap any route that must NOT render until the customer
 * has pinned a service location.
 *
 * Per product (M1): address-first booking. Customers cannot see the
 * service catalog, prices, or available slots until they've picked a
 * location whose coordinates resolve to a covered B2C zone.
 *
 * Guests are allowed: the address lives in localStorage and the gate
 * accepts it. Authenticated users without any saved address are also
 * sent to /address.
 *
 * The current path is forwarded as `?next=` so the gate can route the
 * customer back to where they intended after they confirm an address.
 */
export function AddressGate({ children }: { children: ReactNode }) {
  const { status, address } = useAddress();
  const location = useLocation();

  if (status === 'loading') return <LoadingScreen />;

  if (!address) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${Routes.address.path}?next=${next}`} replace />;
  }

  return <>{children}</>;
}
